import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import { Box, Button, ConfirmPopup, CustomSpinner, Icon, Row } from '~/commonComponents';
import { useNavigationDetector, useToggle, useAppSelector } from '~/hooks';
import { JobDetailsPreview } from '../JobDetailsForm';
import { useGetDraftJobPost, usePublishDraftJobPost } from '~/domain/PermanentJobs';
import { PaymentDetailsPreview } from './components/PaymentDetailsPreview';
import { WorkingInterviewPreviewer } from '../components';
import { showErrorMessage } from '~/domain/utils';
import { hideMessage, showMessage } from '~/actions';
import { clearWorkingInterviews } from '~/actions/PermanentJobs';

/**
 * Helper function to check if a navigation path belongs to the allowed flow routes
 */
const isPathInAllowedFlow = (pathname: string, currentId: string | undefined) => {
  if (!pathname || !currentId) return false;

  // Allow navigation to any path that starts with /permanent-jobs/postings/:id
  const allowedPattern = new RegExp(`^/permanent-jobs/postings/${currentId}`);

  return allowedPattern.test(pathname);
};

export const ReviewAndPaymentPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [confirmationExitModalOpen, toggleConfirmationExitModal] = useToggle(false);

  const { data: draftData, isLoading, isError, error } = useGetDraftJobPost(id);

  // Get working interviews from Redux state
  const workingInterviews = useAppSelector((state) => {
    if (!id) return [];
    return state.permanentJobs.workingInterviews[id] ?? [];
  });

  // Publish draft mutation
  const {
    mutate: publishDraft,
    isPending: isPublishing,
    isError: isPublishError,
    error: publishError,
  } = usePublishDraftJobPost();

  useEffect(() => {
    if (isError) {
      showErrorMessage({ error });
    }
  }, [isError, error, dispatch]);

  useEffect(() => {
    if (isPublishError) {
      showErrorMessage({ error: publishError });
    }
  }, [isPublishError, publishError]);

  // Navigation blocking logic
  const { allowNavigation, clearPendingNavigation, navigateToPending } = useNavigationDetector({
    when: true, // Always block navigation from this page
    onNavigationAttempt: (targetLocation) => {
      const targetPath = targetLocation?.pathname;

      // Check if target is within the allowed flow
      if (isPathInAllowedFlow(targetPath, id)) {
        // Allow navigation within the flow without confirmation
        navigateToPending();
      } else {
        // Show confirmation modal for navigation outside the flow
        toggleConfirmationExitModal.setOn();
      }
    },
  });

  const handleEditJobDetails = (scrollToSection?: string) => {
    // Navigate to job details form with edit mode
    navigate(`/permanent-jobs/postings/${id}`, {
      state: { editMode: true, scrollToSection },
    });
  };

  const handleSubmit = () => {
    if (!id) return;

    // Publish the draft job post
    publishDraft(
      { id, workingInterviews },
      {
        onSuccess: () => {
          dispatch(
            showMessage({
              popupProps: {
                rightButtonText: 'Got it!',
                rightButtonAction: () => {
                  // Allow navigation before navigating to dashboard
                  allowNavigation();
                  dispatch(clearWorkingInterviews(id));
                  navigate('/dashboard');
                  dispatch(hideMessage());
                },
              },
              title: 'You’re All Set!',
              message:
                'Your job and interview requests have been posted successfully. They’re now visible to GoTu professionals. You can visit “Manage Jobs” anytime to edit details, update working interviews, or manage candidates.',
              isSuccess: true,
            }),
          );
        },
      },
    );
  };

  // Show loading state
  if (isLoading || isPublishing) {
    return <CustomSpinner isLoading />;
  }

  return (
    <>
      <Box alignSelf="center" gap={32} width={892}>
        {/* Job Details Preview */}
        <JobDetailsPreview
          data={draftData}
          onEditGeneral={() => handleEditJobDetails('general')}
          onEditSchedule={() => handleEditJobDetails('schedule')}
          onEditDescription={() => handleEditJobDetails('description')}
        />

        {/* Working Interview Preview */}
        <WorkingInterviewPreviewer />

        {/* Payment Details Preview */}
        <PaymentDetailsPreview />

        <Row justifyContent="center" marginTop={6}>
          <Button text="Post Job" onClick={handleSubmit} />
        </Row>
      </Box>

      {/* Confirmation modal for navigation outside the flow */}
      {confirmationExitModalOpen && (
        // @ts-expect-error - ConfirmPopup is not typed
        <ConfirmPopup
          Icon={() => <Icon name="warning-outline" size={80} />}
          leftButtonAction={() => {
            clearPendingNavigation();
            toggleConfirmationExitModal.setOff();
          }}
          leftButtonText="Go Back"
          rightButtonAction={navigateToPending}
          rightButtonText="Leave Anyway"
          title="Are You Sure You Want to Leave?"
          titleStyle={{ marginBottom: 20, marginTop: 20 }}
          descriptionStyle={{ marginBottom: 40 }}
          description="Your job isn’t posted yet, but we saved it as a draft under Manage Jobs. Do you still want to leave this page?"
        />
      )}
    </>
  );
};
